import { resolveApiUrl } from "~/utils/url/urlResolver";
import { occBaseSiteUrl, occFetch, occUrl } from "~/app/api/occ/occFetch";

export interface FetchPageDataOptions {
  headers?: Record<string, string>;
  /** Rename browser query parameters that would collide with OCC transport context. */
  queryParamAliases?: Readonly<Record<string, string>>;
}

export class PageDataRequestError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(url: string, status: number, body: unknown) {
    super(`Could not fetch ${url}: ${status}`);
    this.name = "PageDataRequestError";
    this.status = status;
    this.body = body;
  }
}

/** Reads the stable backend code from either a direct error body or an OCC error list. */
export function getPageDataErrorCode(error: unknown): string | undefined {
  const body = error instanceof PageDataRequestError ? error.body : error;
  if (body === null || typeof body !== "object" || Array.isArray(body)) {
    return undefined;
  }
  const directCode = (body as { errorCode?: unknown }).errorCode;
  if (typeof directCode === "string") {
    return directCode;
  }
  const errors = (body as { errors?: unknown }).errors;
  if (!Array.isArray(errors) || errors.length === 0) {
    return undefined;
  }
  const firstError = errors[0];
  if (firstError === null || typeof firstError !== "object" || Array.isArray(firstError)) {
    return undefined;
  }
  const errorCode = (firstError as { errorCode?: unknown }).errorCode;
  return typeof errorCode === "string" ? errorCode : undefined;
}

export function getPageDataErrorMessage(error: unknown): string | undefined {
  const body = error instanceof PageDataRequestError ? error.body : error;
  if (body === null || typeof body !== "object" || Array.isArray(body)) {
    return undefined;
  }
  const directMessage = (body as { message?: unknown }).message;
  if (typeof directMessage === "string" && directMessage.trim()) {
    return directMessage;
  }
  const errors = (body as { errors?: unknown }).errors;
  if (!Array.isArray(errors) || errors.length === 0) {
    return undefined;
  }
  const firstError = errors[0];
  if (firstError === null || typeof firstError !== "object" || Array.isArray(firstError)) {
    return undefined;
  }
  const message = (firstError as { message?: unknown }).message;
  return typeof message === "string" && message.trim() ? message : undefined;
}

export const FRONTEND_CONTEXT_PATH_HEADER = "X-Schaeffler-Frontend-Context-Path";
export const STOREFRONT_REFERRER_HEADER = "X-Storefront-Referrer";
const PREVIEW_TICKET_PARAMETER = "cmsTicketId";
const RETIRED_BROWSER_PAGE_PATH_PARAMETER = "browserPagePath";

export function sanitizePageDataSearch(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(RETIRED_BROWSER_PAGE_PATH_PARAMETER);
  const remainingSearch = params.toString();
  return remainingSearch ? `?${remainingSearch}` : "";
}

export async function fetchPageData<T>(path: string, options: FetchPageDataOptions = {}): Promise<T> {
  return requestPageData(resolveApiUrl(path), fetch, options);
}

export async function fetchOccPageData<T>(path: string, options: FetchPageDataOptions = {}): Promise<T> {
  return fetchOccPageDataFromUrl(occUrl, path, options);
}

export async function fetchOccResourcePageData<T>(path: string, options: FetchPageDataOptions = {}): Promise<T> {
  return fetchOccPageDataFromUrl(occBaseSiteUrl, path, options);
}

async function fetchOccPageDataFromUrl<T>(
  urlFactory: (path: string) => string,
  path: string,
  options: FetchPageDataOptions,
): Promise<T> {
  const { queryParamAliases, ...requestOptions } = options;
  const frontendContextPath = window.__SCHA_BOOT__?.contextPath ?? "";
  const aliasedPath = applyQueryParamAliases(path, queryParamAliases);
  const [pathname, queryString = ""] = aliasedPath.split("?", 2);
  const sanitizedPath = `${pathname}${sanitizePageDataSearch(queryString)}`;
  return requestPageData(urlFactory(withBrowserPreviewTicket(sanitizedPath)), occFetch, {
    ...requestOptions,
    headers: {
      ...options.headers,
      ...storefrontReferrerHeader(),
      [FRONTEND_CONTEXT_PATH_HEADER]: frontendContextPath,
    },
  });
}

function storefrontReferrerHeader(): Record<string, string> {
  if (typeof document === "undefined" || !document.referrer) {
    return {};
  }
  return { [STOREFRONT_REFERRER_HEADER]: document.referrer };
}

function applyQueryParamAliases(path: string, aliases: Readonly<Record<string, string>> = {}): string {
  const [pathname, queryString = ""] = path.split("?", 2);
  const params = new URLSearchParams(queryString);

  for (const [source, target] of Object.entries(aliases)) {
    if (source === target) {
      continue;
    }
    const values = params.getAll(source);
    params.delete(source);
    for (const value of values) {
      params.append(target, value);
    }
  }

  const mergedQuery = params.toString();
  return mergedQuery ? `${pathname}?${mergedQuery}` : pathname;
}

/** True while the browser URL carries the SmartEdit CMS preview ticket. */
export function hasBrowserPreviewTicket(): boolean {
  return new URLSearchParams(window.location.search).has(PREVIEW_TICKET_PARAMETER);
}

/**
 * SmartEdit keeps the preview ticket on the browser URL while page-data query modules build endpoint paths
 * independently. Forward that one request-context parameter without reintroducing unrelated browser-query state.
 * Endpoint-provided values remain authoritative for routes that explicitly own a ticket.
 */
function withBrowserPreviewTicket(path: string): string {
  const browserTicket = new URLSearchParams(window.location.search).get(PREVIEW_TICKET_PARAMETER);
  if (!browserTicket) {
    return path;
  }

  const [pathname, queryString = ""] = path.split("?", 2);
  const params = new URLSearchParams(queryString);
  if (!params.has(PREVIEW_TICKET_PARAMETER)) {
    params.set(PREVIEW_TICKET_PARAMETER, browserTicket);
  }
  return `${pathname}?${params.toString()}`;
}

async function requestPageData<T>(
  url: string,
  request: (url: string, init?: RequestInit) => Promise<Response>,
  options: FetchPageDataOptions,
): Promise<T> {
  const response = await request(url, {
    // Page-data is query-managed and must not reuse a cached permanent redirect
    // into a locale-prefixed storefront path such as /en/occ/....
    cache: "no-store",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...options.headers,
    },
    credentials: "same-origin",
  });

  if (!response.ok) {
    throw new PageDataRequestError(url, response.status, await readResponseBody(response));
  }

  return response.json() as Promise<T>;
}

async function readResponseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) {
    return undefined;
  }
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
